import AllYesNoCheckbox from "@app/components/all-yes-no-checkbox.tsx";
import FiltersContainer from "@app/components/filters-container.tsx";
import PageTitle from "@app/components/page-title.tsx";
import { defaultAssignmentFilter } from "@app/constants/filters.defaults.tsx";
import { ActiveEnums } from "@app/data/engine.ts";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useClientData from "@app/data-hooks/use-client-data.ts";
import useLookupListData from "@app/data-hooks/use-lookup-list-data.ts";
import { RouteHelper } from "@app/helpers/route-helper.ts";
import { assignmentFiltersStore } from "@app/stores/filters.store.ts";
import { AssignmentsTableDefinition } from "@app/table-definitions/assignments.table-definition.tsx";
import { Autocomplete, Box, Button, TextField } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { DataTable, QueryStatusRouter, Select } from "bi-lib";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/_authenticated/_manager/assignment/")({
    beforeLoad: RouteHelper.ManagerAuthGuard,
    component: Component,
});

function Component() {
    const [clientIdErrorVisible, setClientIdErrorVisible] = useState(false);

    const filters = useStore(assignmentFiltersStore);

    const { extensionsList } = useLookupListData();
    const { clientGrid } = useClientData({ activeState: ActiveEnums.ActiveOnly });
    const { assignmentsList, updateAssignmentStatus } = useAssignmentData({
        clientId: filters.client?.id,
        extension: filters.extension,
        activeState: filters.activeState,
        searchDesc: filters.searchDesc,
    });

    const navigate = useNavigate();

    useEffect(() => {
        setClientIdErrorVisible(false);
    }, [filters.client]);

    const handleCreate = () => {
        if (filters.client == undefined) {
            setClientIdErrorVisible(true);
            return;
        }

        void navigate({
            to: "/assignment/create/$clientId",
            params: { clientId: filters.client.id.toString() },
        });
    };

    return (
        <>
            <PageTitle>Assignments</PageTitle>
            <FiltersContainer onReset={() => assignmentFiltersStore.setState(() => defaultAssignmentFilter)}>
                <Box display={"flex"} gap={2} flexWrap={"wrap"}>
                    <QueryStatusRouter queryHook={extensionsList}>
                        <Select
                            label={"Extension"}
                            value={filters.extension}
                            onChange={(e) =>
                                assignmentFiltersStore.setState(() => ({
                                    ...filters,
                                    extension: e,
                                }))
                            }
                            sx={{ minWidth: 150 }}
                            options={extensionsList.data?.map((x) => ({ label: x.displayName, value: x.extensionId }))}
                        />
                    </QueryStatusRouter>
                    <AllYesNoCheckbox
                        value={filters.activeState}
                        onChange={(value) =>
                            assignmentFiltersStore.setState(() => ({ ...filters, activeState: value }))
                        }
                        label={"Active"}
                    />
                    <TextField
                        label={"Description"}
                        value={filters.searchDesc}
                        onChange={(e) =>
                            assignmentFiltersStore.setState(() => ({ ...filters, searchDesc: e.target.value }))
                        }
                        sx={{ minWidth: 150 }}
                    />
                    <QueryStatusRouter queryHook={clientGrid}>
                        <Autocomplete
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={"Client"}
                                    error={clientIdErrorVisible}
                                    helperText={clientIdErrorVisible && "A client must be selected"}
                                />
                            )}
                            options={
                                clientGrid.data?.map((x) => ({
                                    label: `${x.clientNameShort} - ${x.clientName} (${x.clientNumber})`,
                                    id: x.clientId!,
                                })) ?? []
                            }
                            sx={{ minWidth: 250 }}
                            value={filters.client}
                            onChange={(_e, value) =>
                                assignmentFiltersStore.setState(() => ({ ...filters, client: value ?? undefined }))
                            }
                        />
                    </QueryStatusRouter>
                </Box>
            </FiltersContainer>
            <DataTable
                rowsPerPageOptions={[5000]}
                label={"Assignments"}
                actions={
                    <Button variant={"contained"} onClick={handleCreate}>
                        Add New
                    </Button>
                }
                columns={AssignmentsTableDefinition({
                    onActivateDeactivate: (id, isActive) =>
                        updateAssignmentStatus.mutate({
                            assignmentId: id,
                            isEnabled: isActive,
                        }),
                    rowCount: assignmentsList.data?.length ?? 0,
                })}
                data={assignmentsList.data ?? []}
                queryHook={assignmentsList}
            />
        </>
    );
}
